import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Highlight from './text_viewer/highlight.jsx';
import EditingHandles from './text_viewer/editing_handles.jsx';
import Tether from 'tether';

import { markupRecord } from '../reducers/markup/markup';
import { findSelection } from '../selectors/markup';
import { onTouchStart } from '../utils/longTouch';

import env from '../env';
import { ICONS } from 'const';

const { DEBUG_DRAG_THRESHOLD } = env;

const max = function(a, b) {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    // intA will ALWAYS be numeric because it is initialized with 0.
    // If you flip this condition it will fail when somethingu
    // like NaN comes along.
    return intA < intB ? b : a;
};
const distance = function(a, b) {
    const width = a.x - b.x;
    const height = a.y - b.y;
    return Math.sqrt(height * height + width * width);
};

class TextComponent extends PureComponent {
    static defaultProps = {
        canHighlight: false,
        htmlPopover: false,
    };

    static displayName = 'TextComponent';

    static propTypes = {
        rootStateHack: PropTypes.object,
        newMarkup: PropTypes.func.isRequired,
        update: PropTypes.func.isRequired,
        canHighlight: PropTypes.bool,
        content: PropTypes.any.isRequired, // store
        contentId: PropTypes.number.isRequired,
        highlights: PropTypes.any.isRequired, // store
        htmlPopover: PropTypes.bool,
        imgsrc: PropTypes.string.isRequired, // store
        lastHighlightColor: PropTypes.string, // store
        onLoad: PropTypes.func.isRequired,
        page: PropTypes.any, // store
        pagenum: PropTypes.number.isRequired,
        scale: PropTypes.number.isRequired, // store
        scaleCenter: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
        }), // store (not required)
        hideSharingQA: PropTypes.bool.isRequired,
        zingTouchRegion: PropTypes.object,
        newNotification: PropTypes.func.isRequired,
    };

    state = {
        selection_text: '',
        startSelection: undefined,
        endSelection: undefined,
        movedDistance: 0,
        isMouseDown: false,
        selectedHighlight: null,
        imageUrl: '',
    };

    componentDidMount() {
        const scrollParent = this.textReader.parentNode;
        scrollParent.addEventListener('scroll', this.onScrollPositionTethers);
        this.loadImage();
    }

    componentDidUpdate(prevProps, prevState) {
        // Only update the selection when it changes to prevent the browser's
        // selection from being yanked away from the user.
        if (prevState.selection_text !== this.state.selection_text) {
            this.setSelection();
        }

        const { scale, scaleCenter } = this.props;
        if (prevProps.scale && prevProps.scale !== scale && scaleCenter) {
            const { x, y } = scaleCenter;
            this.textReader.scrollLeft = x * scale;
            document.documentElement.scrollTop = y;
            document.body.scrollTop = y;
        }

        const { zingTouchRegion } = this.props;

        if (!prevProps.zingTouchRegion && zingTouchRegion) {
            zingTouchRegion.bind(
                this.textReader,
                'swipe',
                this.resetPendingHighlight
            );
        }

        if (prevProps.imgsrc !== this.props.imgsrc) {
            this.loadImage();
        }
    }

    componentWillUnmount() {
        const scrollParent = this.textReader.parentNode;
        scrollParent.removeEventListener(
            'scroll',
            this.onScrollPositionTethers
        );
    }

    loadImage = () => {
        // <image onLoad={...} doesn't fire when xlinkHref / href is changed on Safari
        const { imgsrc, pagenum, newNotification, onLoad } = this.props;
        fetch(imgsrc, { cache: 'force-cache' })
            .then(res => res.blob())
            .then(blob => {
                this.setState({ imageUrl: URL.createObjectURL(blob) }, onLoad);
            })
            .catch(() => {
                newNotification('Error', `Error loading page ${pagenum}`, {
                    icon: ICONS.FAIL,
                });
                onLoad();
            });
    };

    onScrollPositionTethers() {
        Tether.position();
    }

    onMouseDown = e => {
        if (Number.isInteger(e.button)) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.startHighlight(e);
        const mouseMoveListener = event => {
            this.onPan({ touches: [event] });
        };
        const { currentTarget } = e;

        const mouseUpListener = () => {
            currentTarget.removeEventListener('mouseup', mouseUpListener);
            currentTarget.removeEventListener('mousemove', mouseMoveListener);
        };

        currentTarget.addEventListener('mousemove', mouseMoveListener);
        currentTarget.addEventListener('mouseup', mouseUpListener);
    };

    onPan = event => {
        if (this.endPanTimeout) {
            clearTimeout(this.endPanTimeout);
        }
        if (this.state.startSelection) {
            event.preventDefault && event.preventDefault();
            const endSelection = this.convertSVGCoords(event.touches[0]);

            let movedDistance = this.state.movedDistance;

            if (movedDistance < this.getDragThreshold()) {
                movedDistance = max(
                    movedDistance,
                    distance(this.state.startSelection, endSelection)
                );
            }

            this.setState({
                endSelection: endSelection,
                movedDistance: movedDistance,
            });
        }
        this.endPanTimeout = setTimeout(() => {
            this.endHighlight();
            this.lastCoords = null;
        }, 500);
    };

    onTouchStart = event =>
        onTouchStart({
            onStart: this.startHighlight,
            onMove: this.onPan,
        })(event);

    onNewHighlight(data) {
        this.props.newMarkup(data);
    }

    onSelectHighlight = (highlight, mode) => {
        const current = this.state.selectedHighlight;
        const nextState = {
            selectedHighlight: highlight !== current ? highlight : null,
            selection_text: highlight.selection_text,
        };
        this.endEdit();
        if (mode === 'edit') {
            nextState.editingHighlight = undefined;
            nextState.startSelection = undefined;
            nextState.endSelection = undefined;
            nextState.movedDistance = undefined;

            const { editingHighlight } = this.state;
            if (!editingHighlight || editingHighlight.cid !== highlight.cid) {
                nextState.movedDistance = this.getDragThreshold();
                nextState.editingHighlight = highlight;
                nextState.startSelection = {
                    x: highlight.x1,
                    y: highlight.y1,
                };
                nextState.endSelection = {
                    x: highlight.x2,
                    y: highlight.y2,
                };
            }
        }
        this.setState(nextState);
    };

    getDragThreshold() {
        // The coordinates here are all on the page scale.
        return this.props.page.width * 0.01;
    }

    setSelection() {
        let range;
        const element = this.selection;
        if (window.getSelection && document.createRange) {
            // Chrome, Firefox, Safari, Opera
            const selection = window.getSelection();
            range = document.createRange();
            range.selectNodeContents(element);
            selection.removeAllRanges();
            selection.addRange(range);
        } else if (document.body.createTextRange) {
            // IE
            range = document.body.createTextRange();
            range.moveToElementText(element);
            range.select();
        }
    }

    UNSAFE_componentWillUpdate() {
        const {
            scrollLeft,
            scrollTop,
            clientHeight,
            clientWidth,
            scrollHeight,
            scrollWidth,
        } = document.body;
        // Store this so the buttons will zoom into the center of the window. // This is state that lives outside of `this.state`, but I don't want
        // to have to update this on scroll or to cause a re-render when it
        // changes.
        this.windowCenter = {
            x: (scrollLeft + clientWidth / 2) / scrollWidth,
            y: (scrollTop + clientHeight / 2) / scrollHeight,
        };
    }

    _sort(a, b) {
        if (a === b) {
            return 0;
        } else if (a < b) {
            return -1;
        }
        return 1;
    }

    convertSVGCoords({ clientX, clientY }) {
        const rect = this.image.getBoundingClientRect();
        const { page } = this.props;

        const x = ((clientX - rect.left) / rect.width) * page.width;
        const y = ((clientY - rect.top) / rect.height) * page.height;

        if (isNaN(x) || isNaN(y)) {
            throw new Error('Invalid coordinates');
        }

        return {
            // The server stores integers, so there's no reason to use anything
            // else here.
            x: Math.floor(x),
            y: Math.floor(y),
        };
    }

    endEdit() {
        if (this.state.editingHighlight) {
            const gen = this.generateHighlight();

            this.props.update(this.state.editingHighlight, {
                selection_text: gen.selection_text,
                x1: this.state.startSelection.x,
                y1: this.state.startSelection.y,
                x2: this.state.endSelection.x,
                y2: this.state.endSelection.y,
            });

            this.resetPendingHighlight();
        }
    }

    endHighlight = () => {
        this.setState(state => {
            if (state.startSelection) {
                const highlight = this.generateHighlight();
                if (highlight.selections.length > 0) {
                    this.onNewHighlight(highlight);
                }

                return {
                    selection_text: highlight.selection_text,
                };
            }
            return {};
        }, this.resetPendingHighlight);
    };

    generateHighlight() {
        const state = this.state;
        let selections = [];

        if (this.state.movedDistance >= this.getDragThreshold()) {
            selections = findSelection(
                this.props.rootStateHack,
                this.props.content,
                this.props.pagenum,
                {
                    x1: state.startSelection.x,
                    y1: state.startSelection.y,
                    x2: state.endSelection.x,
                    y2: state.endSelection.y,
                }
            );
        }

        return {
            content_id: this.props.contentId,
            color: this.props.lastHighlightColor,
            selections: selections,
            page: this.props.pagenum,
            x1: state.startSelection.x,
            y1: state.startSelection.y,
            x2: state.endSelection.x,
            y2: state.endSelection.y,
            selection_text: selections
                .map(function(line) {
                    return line[4].join(' ');
                })
                .join(' '),
        };
    }

    imageRef = el => {
        this.image = el;
    };

    resetPendingHighlight = () => {
        this.setState({
            startSelection: undefined,
            endSelection: undefined,
            movedDistance: 0,
            isMouseDown: false,
            editingHighlight: undefined,
        });
    };

    selectionRef = el => {
        this.selection = el;
    };

    sortByReadOrder = (a, b) => {
        return this._sort(a.y1, b.y1) || this._sort(a.x1, b.x1);
    };

    startHighlight = event => {
        const { x, y } = this.convertSVGCoords(event);
        if (!this.props.canHighlight) {
            return;
        }

        this.endEdit();
        this.setState({
            selection_text: '',
            startSelection: { x, y },
            endSelection: { x, y },
            isMouseDown: true,
            editingHighlight: undefined,
        });
    };

    svgRef = el => {
        this.svg = el;
    };

    textReaderRef = el => {
        this.textReader = el;
    };

    updateHighlightPosition(start, end) {
        this.setState({
            startSelection: {
                x: start.x,
                y: start.y,
            },
            endSelection: {
                x: end.x,
                y: end.y,
            },
        });
    }

    renderHighlights = () => {
        const { editingHighlight } = this.state;
        const highlights = this.props.highlights.sort(this.sortByReadOrder);

        let editedHighlight;

        const tmp = highlights.map(h => {
            if (editingHighlight && h.cid === editingHighlight.cid) {
                const generated = this.generateHighlight();

                h = editedHighlight = {
                    ...h,
                    selections: generated.selections,
                    selection_text: generated.selection_text,
                };
            }

            return (
                <Highlight
                    onSelect={this.onSelectHighlight}
                    highlight={h}
                    key={h.cid}
                    useHtmlPopover={this.props.htmlPopover}
                    hideSharingQA={this.props.hideSharingQA}
                    zingTouchRegion={this.props.zingTouchRegion}
                />
            );
        });

        if (editedHighlight) {
            tmp.push(
                <EditingHandles
                    setSelection={this.setSelection}
                    onMove={this.updateHighlightPosition}
                    convertSVGCoords={this.convertSVGCoords}
                    key={this.state.editingHighlight.cid + '-handles'}
                    highlight={editedHighlight}
                />
            );
        } else if (this.state && this.state.startSelection) {
            const highlight = markupRecord(this.generateHighlight());
            // Nothing can be displayed if it doesn't intersect any words.
            if (highlight.selections.length > 0) {
                tmp.push(
                    <Highlight
                        highlight={highlight}
                        key={-1}
                        onSelect={function() {}}
                        hideSharingQA={this.props.hideSharingQA}
                    />
                );
            }

            // grunt setenv:DEBUG_DRAG_THRESHOLD:true default
            if (DEBUG_DRAG_THRESHOLD) {
                tmp.push(
                    <circle
                        key="debug-drag-threshold"
                        cx={this.state.startSelection.x}
                        cy={this.state.startSelection.y}
                        r={this.getDragThreshold()}
                        style={{
                            fill: 'none',
                            strokeWidth: 1,
                            stroke: 'black',
                        }}
                    />
                );
            }
        }

        return tmp;
    };

    render() {
        const {
            page: { height, width },
            scale,
        } = this.props;

        return (
            <div
                className="svg-reader"
                ref={this.textReaderRef}
                onTouchStart={this.onTouchStart}
                onMouseDown={this.onMouseDown}
            >
                <svg
                    ref={this.svgRef}
                    preserveAspectRatio="xMidYMin slice"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox={'0 0 ' + width + ' ' + height}
                    id="svg2"
                    version="1.1"
                    style={{
                        height: scale * 100 + '%',
                        width: scale * 100 + '%',
                    }}
                >
                    <image
                        xlinkHref={this.state.imageUrl}
                        // xlinkHref is deprecated, but Safari doesn't implement href yet.
                        href={this.state.imageUrl}
                        ref={this.imageRef}
                        width={width}
                        height={height}
                        x={0}
                        y={0}
                    />
                    {this.renderHighlights()}
                </svg>
                <div ref={this.selectionRef} className="clipboard">
                    {this.state.selection_text}
                </div>
            </div>
        );
    }
}

export default TextComponent;



// WEBPACK FOOTER //
// ./src/components/text_viewer.jsx